import i18n from '@/i18n/config';
import { getTypeCode, typeLabel } from '@/components/booking-grid/helpers';

/** Rekkefølge for visning/sortering av Type-koder (som i Booking-listen). */
export const TYPE_ORDER = ['P', 'T', 'ST', 'MB', 'SMB', 'B', 'SB'];

/** Turens Type = kjøretøyet turen kjøres med (T/MB/B, S=delt) — backend `type_code`, ellers beregnet fra bookingene.
 *  Iter 846: aldri «P/MB»-kombinasjoner — P (per passasjer) teller kun når alle bookingene er P; ved ulike klasser vinner største, delt foran ikke-delt. */
export function tripTypeCode(trip: any): string {
  if (trip?.type_code) return String(trip.type_code);
  const codes = new Set<string>();
  (trip?.bookings || []).forEach((b: any) => { const c = getTypeCode(b)?.code; if (c) codes.add(c); });
  const vehicleCodes = [...codes].filter((c) => c !== 'P');
  if (!vehicleCodes.length) return codes.size ? 'P' : '';
  const bases = ['T', 'MB', 'B'];
  const base = vehicleCodes.map((c) => c.replace(/^S/, '')).sort((a, b) => bases.indexOf(b) - bases.indexOf(a))[0];
  return vehicleCodes.includes(`S${base}`) ? `S${base}` : base;
}

export function tripTypeCodes(trip: any): string[] {
  return tripTypeCode(trip).split('/').map((c) => c.trim().toUpperCase()).filter(Boolean);
}

export function tripTypeTitle(code: string): string {
  return code.split('/').filter(Boolean).map((c) => `${c}: ${typeLabel(c)}`).join(' · ');
}

// Typefilter (iter 845): '' = alle · gruppe (delt/taxi/minibuss/buss/perpax) · eller eksakt kode (T/ST/MB/SMB/B/SB/P).
export const TYPE_FILTER_GROUPS: Record<string, string[]> = {
  delt: ['ST', 'SMB', 'SB'],
  taxi: ['T', 'ST'],
  minibuss: ['MB', 'SMB'],
  buss: ['B', 'SB'],
  perpax: ['P'],
};

const GROUP_LABELS: Record<string, { no: string; en: string }> = {
  delt: { no: 'Delte turer', en: 'Shared trips' },
  taxi: { no: 'Taxi', en: 'Taxi' },
  minibuss: { no: 'Minibuss', en: 'Minibus' },
  buss: { no: 'Buss', en: 'Bus' },
  perpax: { no: 'Per passasjer', en: 'Per passenger' },
};

export function typeFilterLabel(filter: string, lang: string): string {
  if (!filter) return '';
  const g = GROUP_LABELS[filter];
  if (g) return lang === 'en' ? g.en : g.no;
  return `Type ${filter.toUpperCase()}`;
}

export function tripMatchesType(trip: any, filter: string): boolean {
  if (!filter) return true;
  const wanted = TYPE_FILTER_GROUPS[filter] || [filter.toUpperCase()];
  return tripTypeCodes(trip).some((c) => wanted.includes(c));
}

/** Koder som forekommer i perioden (ufiltrert plan) — grunnlag for «Nøyaktig kode» i nedtrekksmenyen. */
export function typeCodesInPlan(weeklyPlan: any): string[] {
  const seen = new Set<string>();
  for (const day of weeklyPlan?.days || []) for (const trip of day.trips || []) tripTypeCodes(trip).forEach((c) => seen.add(c));
  return [...seen].sort((a, b) => TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));
}

// ── Iter 853: forklaring («legend») av Type-kodene i alt kundevendt — kilde: i18n `typeCode.*` (NO/EN). ──
const LEGEND_TITLE: Record<string, string> = { no: 'Forklaring Type', en: 'Type legend' };
const uiLang = (lang: string) => (String(lang || '').toLowerCase().startsWith('en') ? 'en' : 'no');

/** Normaliserer vilkårlige verdier («P/MB», små bokstaver, tomme) til unike gyldige koder i fast rekkefølge. */
export function normalizeTypeCodes(values: Iterable<string>): string[] {
  const seen = new Set<string>();
  for (const v of values) String(v || '').toUpperCase().split('/').map((c) => c.trim()).filter((c) => TYPE_ORDER.includes(c)).forEach((c) => seen.add(c));
  return [...seen].sort((a, b) => TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));
}

export function typeLegendTitle(lang: string): string {
  return LEGEND_TITLE[uiLang(lang)];
}

/** Kun kodene som faktisk forekommer — etikett fra i18n i ønsket språk (ikke UI-språket). */
export function typeLegendEntries(codes: Iterable<string>, lang: string): Array<{ code: string; label: string }> {
  const ft = i18n.getFixedT(uiLang(lang));
  return normalizeTypeCodes(codes).map((code) => ({ code, label: ft(`typeCode.${code}`, typeLabel(code)) }));
}

/** «Forklaring Type: P = Per-passasjer (…) · ST = Delt taxi» — tom streng uten koder. */
export function typeLegendText(codes: Iterable<string>, lang: string): string {
  const entries = typeLegendEntries(codes, lang);
  return entries.length ? `${typeLegendTitle(lang)}: ${entries.map((e) => `${e.code} = ${e.label}`).join(' · ')}` : '';
}
